import { AdminOverviewScreen } from "@/features/admin-overview"
import {
  Activity,
  ArrowLeftRight,
  BarChart3,
  CircleDollarSign,
  ClipboardCheck,
  CreditCard,
  FileText,
  ListChecks,
  MessageSquareText,
  MonitorSmartphone,
  Radio,
  ReceiptText,
  RotateCcw,
  Scale,
  ScrollText,
  Settings,
  ShieldCheck,
  Users,
} from "lucide-react"
import { ReconciliationScreen } from "@/features/admin-ledger"
import { PaymentDetailScreen, PaymentsScreen, ReviewScreen } from "@/features/admin-payments"
import { PlansScreen, PricesScreen } from "@/features/admin-prices"
import { ReportsScreen } from "@/features/admin-reports"
import { SettingScreen, SettingsScreen } from "@/features/admin-settings"
import { AdminPageScreen, AdminPagesScreen } from "@/features/pages"
import { AdminRefundScreen, AdminRefundsScreen } from "@/features/admin-refunds"
import { TransferDetailScreen, TransfersScreen } from "@/features/admin-transfers"
import { AllDevicesScreen, AllMessagesScreen, AllWebhooksScreen } from "@/features/admin-gateway"
import { AuditScreen, JobScreen, JobsScreen, StatusScreen } from "@/features/admin-ops"
import { RoleEditorScreen, RoleListScreen } from "@/features/admin-roles"
import { UserDetailScreen, UserListScreen } from "@/features/admin-users"
import { nav, type RouteDef } from "./route-kit"

/** The staff half of the route table (LLD §7.2): administration, money, operations. */
export const staffRoutes: RouteDef[] = [
  // Administration
  {
    path: "/admin",
    element: AdminOverviewScreen,
    staff: true,
    phoneSafe: true,
    owner: "6.1",
    nav: nav("administration", "adminOverview", ShieldCheck, 1),
  },
  {
    path: "/admin/users",
    element: UserListScreen,
    permission: "identity.user:read",
    phoneSafe: false,
    owner: "2.3",
    nav: nav("administration", "users", Users, 2, "u"),
  },
  {
    path: "/admin/users/:id",
    element: UserDetailScreen,
    permission: "identity.user:read",
    phoneSafe: false,
    owner: "2.3",
    titleKey: "titles.user",
  },
  {
    path: "/admin/roles",
    element: RoleListScreen,
    permission: "identity.role:read",
    phoneSafe: false,
    owner: "2.4",
    nav: nav("administration", "roles", ClipboardCheck, 3),
  },
  {
    path: "/admin/roles/new",
    element: RoleEditorScreen,
    permission: "identity.role:read",
    phoneSafe: false,
    owner: "2.4",
    titleKey: "titles.role",
  },
  {
    path: "/admin/roles/:id",
    element: RoleEditorScreen,
    permission: "identity.role:read",
    phoneSafe: false,
    owner: "2.4",
    titleKey: "titles.role",
  },
  {
    path: "/admin/devices",
    element: AllDevicesScreen,
    permission: "devices.any:read",
    phoneSafe: false,
    owner: "6.6",
    nav: nav("administration", "allDevices", MonitorSmartphone, 4),
  },
  {
    path: "/admin/messages",
    element: AllMessagesScreen,
    permission: "messaging.any:read",
    phoneSafe: false,
    owner: "6.6",
    nav: nav("administration", "allMessages", MessageSquareText, 5),
  },
  {
    path: "/admin/webhooks",
    element: AllWebhooksScreen,
    permission: "webhooks.any:read",
    phoneSafe: false,
    owner: "6.6",
    nav: nav("administration", "webhookDeliveries", Radio, 6),
  },
  {
    path: "/admin/plans",
    element: PlansScreen,
    permission: "billing.plan:write",
    phoneSafe: false,
    owner: "6.5",
    nav: nav("administration", "planEditor", ReceiptText, 7),
  },

  // Money (normal mode)
  {
    path: "/admin/payments",
    element: PaymentsScreen,
    permission: "payments.payment:read",
    money: true,
    phoneSafe: false,
    owner: "6.2",
    nav: nav("money", "payments", CreditCard, 1),
  },
  {
    path: "/admin/payments/review",
    element: ReviewScreen,
    permission: "payments.payment:read",
    money: true,
    phoneSafe: false,
    owner: "6.2",
    nav: nav("money", "reviewQueue", ListChecks, 2),
  },
  {
    path: "/admin/payments/:id",
    element: PaymentDetailScreen,
    permission: "payments.payment:read",
    money: true,
    phoneSafe: true,
    owner: "6.2",
    titleKey: "titles.payment",
  },
  {
    path: "/admin/transfers",
    element: TransfersScreen,
    permission: "transfers.transfer:review",
    money: true,
    phoneSafe: true,
    owner: "6.3",
    nav: nav("money", "transfers", ArrowLeftRight, 3, "t"),
  },
  {
    path: "/admin/transfers/:id",
    element: TransferDetailScreen,
    permission: "transfers.transfer:review",
    money: true,
    phoneSafe: true,
    owner: "6.3",
    titleKey: "titles.transfer",
  },
  {
    path: "/admin/refunds",
    element: AdminRefundsScreen,
    permission: "refunds.refund:review",
    money: true,
    phoneSafe: true,
    owner: "6.4",
    nav: nav("money", "adminRefunds", RotateCcw, 4),
  },
  {
    path: "/admin/refunds/:id",
    element: AdminRefundScreen,
    permission: "refunds.refund:review",
    money: true,
    phoneSafe: true,
    owner: "6.4",
    titleKey: "titles.refund",
  },
  {
    path: "/admin/ledger",
    element: ReconciliationScreen,
    permission: "ledger.reconciliation:read",
    money: true,
    phoneSafe: false,
    owner: "6.2",
    nav: nav("money", "reconciliation", Scale, 5),
  },
  {
    path: "/admin/prices",
    element: PricesScreen,
    permission: "pricing.price:write",
    money: true,
    phoneSafe: false,
    owner: "6.5",
    nav: nav("money", "prices", CircleDollarSign, 6),
  },
  {
    path: "/admin/reports",
    element: ReportsScreen,
    permission: "reports.report:read",
    money: true,
    phoneSafe: false,
    owner: "6.5",
    nav: nav("money", "reports", BarChart3, 7),
  },

  // Operations
  {
    path: "/admin/jobs",
    element: JobsScreen,
    permission: "system.job:read",
    phoneSafe: false,
    owner: "6.6",
    nav: nav("operations", "jobs", ListChecks, 1),
  },
  {
    path: "/admin/jobs/:id",
    element: JobScreen,
    permission: "system.job:read",
    phoneSafe: false,
    owner: "6.6",
    titleKey: "titles.job",
  },
  {
    path: "/admin/status",
    element: StatusScreen,
    permission: "system.status:read",
    phoneSafe: false,
    owner: "6.6",
    nav: nav("operations", "status", Activity, 2),
  },
  {
    path: "/admin/audit",
    element: AuditScreen,
    permission: "audit.log:read",
    phoneSafe: false,
    owner: "6.6",
    nav: nav("operations", "audit", ScrollText, 3),
  },
  {
    path: "/admin/settings",
    element: SettingsScreen,
    permission: "settings.app:read",
    phoneSafe: false,
    owner: "6.5",
    nav: nav("operations", "settings", Settings, 4),
  },
  {
    path: "/admin/settings/:key",
    element: SettingScreen,
    permission: "settings.app:read",
    phoneSafe: false,
    owner: "6.5",
    titleKey: "titles.setting",
  },
  {
    path: "/admin/pages",
    element: AdminPagesScreen,
    permission: "content.page:write",
    phoneSafe: false,
    owner: "6.5",
    nav: nav("operations", "pages", FileText, 5),
  },
  {
    path: "/admin/pages/:slug",
    element: AdminPageScreen,
    permission: "content.page:write",
    phoneSafe: false,
    owner: "6.5",
    titleKey: "titles.page",
  },
]
